import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SplitTool = ({ pdfInfo, onSplitComplete }) => {
  const [splitPages, setSplitPages] = useState('');
  const [splitting, setSplitting] = useState(false);

  const handleSplit = async () => {
    if (!pdfInfo || !splitPages.trim()) {
      toast.error('Please enter page ranges to split');
      return;
    }

    setSplitting(true);
    try {
      const formData = new FormData();
      formData.append('file_id', pdfInfo.id);
      formData.append('pages', splitPages);

      const response = await axios.post(`${API}/pdf/split`, formData);
      toast.success(`PDF split into ${response.data.files.length} file(s)!`);
      
      // Download all split files
      for (const file of response.data.files) {
        const downloadResponse = await axios.get(`${API}/download/${file.id}`, {
          responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', file.filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      
      setSplitPages('');
      onSplitComplete && onSplitComplete();
    } catch (error) {
      console.error('Split error:', error);
      toast.error(error.response?.data?.detail || 'Failed to split PDF');
    } finally {
      setSplitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="split-tool">
      <div>
        <Label htmlFor="split-pages">Page Ranges</Label>
        <Input
          id="split-pages"
          data-testid="split-pages-input"
          placeholder="e.g., 1-3, 5, 7-9"
          value={splitPages}
          onChange={(e) => setSplitPages(e.target.value)}
          className="mt-1.5"
        />
        <p className="text-xs text-muted-foreground mt-1.5">
          Enter page numbers or ranges separated by commas
        </p>
      </div>

      {pdfInfo && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Total pages:</span>
          <Badge variant="secondary">{pdfInfo.num_pages}</Badge>
        </div>
      )}

      <Button
        onClick={handleSplit}
        className="w-full"
        disabled={!splitPages.trim() || splitting}
        data-testid="split-execute-button"
      >
        {splitting ? 'Splitting...' : 'Split PDF'}
      </Button>

      <div className="bg-accent/50 p-3 rounded-md space-y-1">
        <p className="text-xs font-semibold text-foreground">Examples:</p>
        <ul className="text-xs text-muted-foreground space-y-1 ml-3">
          <li>• <code>1-3</code> - Pages 1 to 3</li>
          <li>• <code>1,3,5</code> - Individual pages</li>
          <li>• <code>1-3,5-7</code> - Multiple ranges</li>
        </ul>
      </div>
    </div>
  );
};

export default SplitTool;